import { skipToken } from '@reduxjs/toolkit/dist/query';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { FC } from 'react';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { ProductionRun } from 'types/production';
import _ from 'lodash';
import { GridLegacy } from '@mui/material';
import { gridSpacing, infoSectionRowSx } from 'constants/themeConstants';
import InfoSectionItem from 'ui-component/InfoSectionItem';
import EditInline from 'ui-component/EditInline';
import { HookFormInput } from 'ui-component/HookFormComponents';
import { PurchaseOrder } from 'types/procurement';
import { OrgSupplier } from 'types/suppliers';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

export enum CustomFieldTypes {
  string = 'string',
}

export enum RecordTypes {
  productionRun = 'productionRun',
  purchaseOrder = 'purchaseOrder',
  orgSupplier = 'orgSupplier',
  purchaseOrderLine = 'purchaseOrderLine',
  purchaseOrderNoPartLine = 'purchaseOrderNoPartLine',
  purchaseOrderCofactrCharge = 'purchaseOrderCofactrCharge',
  purchaseRequest = 'purchaseRequest',
  supplierBill = 'supplierBill',
  team = 'team',
}

export interface CustomFieldDefinition {
  fieldName: string;
  fieldLabel: string;
  fieldType: CustomFieldTypes;
  defaultValue: string;
}
interface DetailCustomFieldsProps {
  // the `any` here seems to be the type used in the RTK Query type definitions
  useMutation: UseMutation<any>;
  record: ProductionRun | PurchaseOrder | OrgSupplier | undefined | null;
  recordType: RecordTypes;
  isLoadingRecord: boolean;
  disableEdit?: boolean;
  idProp?: string;
  otherProps?: object;
}

const DetailCustomFields: FC<DetailCustomFieldsProps> = ({
  useMutation,
  record,
  recordType,
  isLoadingRecord,
  disableEdit,
  idProp = 'id',
  otherProps = {},
}) => {
  const tempCustomProperties = useFeature(FeatureFlags.tempCustomProperties).on;
  const { activeOrg } = useSelector((state) => state.org);

  const { data: metadataOrg, isLoading: isLoadingMetadataOrg } = useGetOrgQuery(
    activeOrg?.createdFrom
      ? typeof activeOrg.createdFrom === 'string'
        ? activeOrg.createdFrom
        : activeOrg.createdFrom.id
      : skipToken
  );
  const customFieldDefinitions = _.get(
    (metadataOrg || activeOrg)?.internalMetadata,
    ['customFields', recordType],
    []
  ) as CustomFieldDefinition[];
  const recordCustomFields = _.get(
    record,
    ['externalMetadata', 'customFields'],
    {}
  );
  if (customFieldDefinitions.length < 1 || tempCustomProperties) {
    return <></>;
  }

  return (
    <GridLegacy
      container
      item
      columnSpacing={gridSpacing}
      sx={infoSectionRowSx}
    >
      {customFieldDefinitions.map((f) => (
        <InfoSectionItem
          key={f.fieldName}
          caption={f.fieldLabel}
          isLoading={isLoadingRecord || isLoadingMetadataOrg}
          value={_.get(recordCustomFields, f.fieldName, f.defaultValue)}
          disableEdit={disableEdit}
          editInline={
            <EditInline
              useMutation={useMutation}
              name="externalMetadata"
              label={f.fieldLabel}
              hideLabel
              value={_.get(recordCustomFields, f.fieldName, f.defaultValue)}
              id={record?.id || ''}
              preSubmit={(data) => {
                let currentMetadata =
                  _.cloneDeep(record?.externalMetadata) || {};
                currentMetadata = _.set(
                  currentMetadata,
                  ['customFields', f.fieldName],
                  data
                );
                return {
                  externalMetadata: currentMetadata,
                };
              }}
              idProp={idProp}
              otherProps={otherProps}
            >
              <HookFormInput disableRequiredProps />
            </EditInline>
          }
        />
      ))}
    </GridLegacy>
  );
};
export default DetailCustomFields;
